import mixpanel, { Config, Dict } from "mixpanel-browser"

import { BookingRequest } from "commonModule/services/booking/booking"
import { BookingPayloadType } from "commonModule/services/booking/types"
import { isDev } from "utils/ssr"

import { EventENUM } from "./mixpanel/types"

const CONFIG: Partial<Config> = {
  api_host: "https://wet.whimstay.com",
  cdn: "https://wet.whimstay.com/cdn",
  ignore_dnt: true,
  persistence: "localStorage",
}

mixpanel.init(
  process.env.MIXPANEL_TOKEN || process.env.NEXT_PUBLIC_MIXPANEL_TOKEN || "",
  CONFIG,
)

const envCheck = !isDev()

class MixpanelV1 {
  private identify(id: string | number) {
    if (envCheck) mixpanel.identify(`${id}`)
  }

  track(name: string, props: Dict = {}) {
    if (envCheck) {
      if (!props.channel) {
        // eslint-disable-next-line no-param-reassign
        props.channel = "web"
      }
      mixpanel.track(name, props)
    }
  }

  private people = {
    set: (props: Dict) => {
      if (envCheck) mixpanel.people.set(props)
    },
    increment: (props: Dict | string) => {
      if (envCheck) mixpanel.people.increment(props)
    },
  }

  private reset() {
    if (envCheck) mixpanel.reset()
  }

  LOGOUT(data: {
    logoutKey: "Logout" | "Force Logout"
    method?: string
    url?: string
  }) {
    this.track(EventENUM.LOGOUT, data)
  }

  EMAIL_VERIFICATION(data: {
    email: string
    userId: number
    verifiedEmail: boolean
  }) {
    this.track(EventENUM.EMAIL_VERIFICATION, {
      Channel: "web",
      "Verified email": data.verifiedEmail,
      UserID: data.userId,
      Email: data.email,
    })
  }

  LOGIN_WITH_EMAIL(data: { email: string; page: string }) {
    this.track(EventENUM.LOGIN_WITH_EMAIL, {
      page: data.page,
      "Login Email": data.email,
    })
  }

  CONTINUE_WITH_EMAIL_FOR_LOGIN(data: { page: string }) {
    this.track(EventENUM.CONTINUE_WITH_EMAIL_FOR_LOGIN, data)
  }

  SESSION_CLOSED(data: { length: number }) {
    this.track(EventENUM.SESSION_CLOSED, {
      "Session Length": data.length,
    })
  }

  WEB_SESSION_END() {
    this.track(EventENUM.WEB_SESSION_END)
  }

  INSPIRATION_PRESSED(data: { city: string; path: string }) {
    this.track(EventENUM.INSPIRATION_PRESSED, data)
  }

  PERMANENTLY_DELETE_PROFILE(data: {
    emailId?: string
    firstName?: string
    lastName?: string
    mobileno1?: string
  }) {
    this.track(EventENUM.PERMANENTLY_DELETE_PROFILE, {
      "First Name": data.firstName,
      "Last Name": data.lastName,
      Email: data.emailId,
      "Phone Number": data.mobileno1,
    })
  }

  SEARCH_PAGE_RESULT(data: {
    noOfBedrooms: number | undefined
    city: string
    latitude: number
    noOfBathrooms: number | undefined
    propertyCount: number | undefined
    sortBy: string
    state: string
    filters: string
    longitude: number
  }) {
    this.track(EventENUM.SEARCH_PAGE_RESULT, {
      "Searched City": data.city,
      "Searched State": data.state,
      "Number of properties display": data.propertyCount,
      "Number of Bedrooms Selected": data.noOfBedrooms,
      "Number Of Bathrooms Selected": data.noOfBathrooms,
      "Types Of Filters": data.filters,
      latitude: data.latitude,
      longitude: data.longitude,
      "sort results by": data.sortBy,
    })
  }

  CLICKED_SHARE_BUTTON(data: {
    addressFull: string
    city: string
    maxNoGuests: number
    noOfBathrooms: number
    noOfBedrooms: number
    noOfReviews: number
    propertyId: string
    propertyName: string
    propertyRatings?: number | string
    state: string
  }) {
    this.track(EventENUM.CLICKED_SHARE_BUTTON, {
      "Property ID": data.propertyId,
      "Property Name": data.propertyName,
      "Property state": data.state,
      "Property city": data.city,
      Location: data.addressFull,
      Guests: data.maxNoGuests,
      Bedrooms: data.noOfBedrooms,
      Bathrooms: data.noOfBathrooms,
      "Average Rating": data.propertyRatings,
      "# of Reviews": data.noOfReviews || 0,
    })
  }

  MAP_MARKER_CLICKED(data: {
    _id: string
    city: string
    country?: string
    propertyName: string
    state: string
  }) {
    this.track(EventENUM.MAP_MARKER_CLICKED, {
      "Property ID": data._id,
      "Property Name": data.propertyName,
      "Searched City": data.city,
      "Searched State": data.state,
      "Searched Country": data.country,
    })
  }

  ABOUT_US_CLICKED(data: {
    source: "Header" | "Footer"
    deviceType?: "Desktop" | "Mobile"
  }) {
    this.track(EventENUM.ABOUT_US_CLICKED, data)
  }

  PROMO_CODE_CLICKED(data: {
    Source: "Header" | "Footer"
    deviceType?: "Desktop" | "Mobile"
  }) {
    this.track(EventENUM.PROMO_CODE_CLICKED, data)
  }

  LANDING_PAGE() {
    this.track(EventENUM.LANDING_PAGE)
  }

  CALENDER(data: { endDate: string; startDate: string }) {
    this.track(EventENUM.CALENDER, {
      "Check In": data.startDate,
      "Check Out": data.endDate,
      "Date Selected?": true,
      Channel: "web",
    })
  }

  FAQS_CLICKED(data: {
    Source: "Header" | "Footer"
    deviceType?: "Desktop" | "Mobile"
  }) {
    this.track(EventENUM.FAQS_CLICKED, data)
  }

  SITEMAP_CLICKED(data: {
    deviceType?: "Desktop" | "Mobile"
    source: "Header" | "Footer"
  }) {
    this.track(EventENUM.SITEMAP_CLICKED, {
      Source: data.source,
      ...(data.deviceType ? { deviceType: data.deviceType } : {}),
    })
  }

  BLOG_CLICKED_DESKTOP(data: { source: "Header" | "Footer" }) {
    this.track(EventENUM.BLOG_CLICKED_DESKTOP, {
      Source: data.source,
      deviceType: "Desktop",
    })
  }

  BLOG_CLICKED_MOBILE(data: { source: "Header" | "Footer" }) {
    this.track(EventENUM.BLOG_CLICKED_MOBILE, {
      Source: data.source,

      deviceType: "Mobile",
    })
  }

  INSTAGRAM_LINK_CLICKED() {
    this.track(EventENUM.INSTAGRAM_LINK_CLICKED)
  }

  FACEBOOK_LINK_CLICKED() {
    this.track(EventENUM.FACEBOOK_LINK_CLICKED)
  }

  LINKEDIN_LINK_CLICKED() {
    this.track(EventENUM.LINKEDIN_LINK_CLICKED)
  }

  PINTEREST_LINK_CLICKED() {
    this.track(EventENUM.PINTEREST_LINK_CLICKED)
  }

  TWITTER_LINK_CLICKED() {
    this.track(EventENUM.TWITTER_LINK_CLICKED)
  }

  TIKTOK_LINK_CLICKED() {
    this.track(EventENUM.TIKTOK_LINK_CLICKED)
  }

  IDENTIFY(id: string | number) {
    this.identify(id)
  }

  SIGNUP_INITIATE() {
    this.track(EventENUM.SIGNUP_INITIATE)
  }

  // to reset current mixpanel session.
  RESET_USER() {
    this.reset()
  }

  // to set users data in mixpanel
  SET_PEOPLE(data: Dict) {
    this.people.set(data)
  }

  INCREMENT(data: string | Dict) {
    const distinctId = mixpanel.get_distinct_id() as string
    if (distinctId) mixpanel.identify(distinctId)
    this.people.increment(data)
  }

  SIGNUP_LINK_CLICKED() {
    this.track(EventENUM.SIGNUP_LINK_CLICKED, {
      Channel: "web",
      deviceType: "Mobile",
    })
  }

  SET_VERIFIED_EMAIL(isVerified: boolean) {
    this.people.set({
      ["$Verified_email"]: isVerified,
    })
  }

  GOOGLE_SSO_CLICKED() {
    this.track(EventENUM.GOOGLE_SSO_CLICKED)
  }

  CONTACT_US_FORM_SUBMISSION(data: {
    checkInDate: Date | string
    checkOutDate: string
    destination: string
    email: string
    fullName: string
    guest: number
    message: string
    nickname: string
    phoneNumber: string
    pronouns: string
    propertyName: string
    propertyId: string
  }) {
    this.track(EventENUM.CONTACT_US_FORM_SUBMISSION, {
      "Full Name": data.fullName,
      Email: data.email,
      "Phone Number": data.phoneNumber,
      "Travel Destination": data.destination,
      "Check in Date": data.checkInDate,
      "Check Out Date": data.checkOutDate,
      "Number of Guests": data.guest,
      Message: data.message,
      Pronouns: data.pronouns,
      Nickname: data.nickname,
      "Property Name": data.propertyName,
      "Property ID": data.propertyId,
    })
  }

  PROFILE_UPDATE(data: {
    dobDt: string
    emailId: string
    firstName: string
    lastName: string
    nickName: string
    numberOfFamilyMembers: string
    numberOfPets: string
    pronouns: string
  }) {
    this.track(EventENUM.PROFILE_UPDATE, {
      $dobDt: data.dobDt,
      Email: data.emailId,
      "First Name": data.firstName,
      "Last Name": data.lastName,
      "Number of family members": data.numberOfFamilyMembers,
      "Number of pets": data.numberOfPets,
      nickName: data.nickName,
      pronouns: data.pronouns,
    })
  }

  RECOMMENDED_STAYS(data: {
    favPropertyIds: number
    isFavourite: boolean
    propertyId: string
  }) {
    this.track(EventENUM.RECOMMENDED_STAYS, {
      "Property ID": data.propertyId,
      "Is Favorite": data.isFavourite,
      "Total Favorites": data.favPropertyIds,
    })
  }

  SEARCH_CLICKED(data: {
    addDays: number
    adultsCount: number
    checkInDate: string
    checkOutDate: string
    childrenCount: number
    city: string
    country: string
    dateSelected: boolean
    latitude: number
    longitude: number
    petsAllowed: boolean
    searchTermOrDestionation: string
    searchedCity: string
    searchedState: string
    state: string
  }) {
    this.track(EventENUM.SEARCH_CLICKED, {
      "Add day": data.addDays,
      "Search Term or Destination": data.searchTermOrDestionation,
      "Check In": data.checkInDate,
      "Check Out": data.checkOutDate,
      "Adults Count": data.adultsCount,
      "Children Count": data.childrenCount,
      "Pets Allowed": data.petsAllowed,
      "Date Selected": data.dateSelected,
      "Search of city": data.city,
      "Search of State": data.state,
      "Searched Country": data.country,
      latitude: data.latitude,
      longitude: data.longitude,
      "Searched City": data.searchedCity,
      "Searched State": data.searchedState,
    })
  }

  INTERESTED_IN_PROPERTY_PICTURES(data: { index: number; propertyId: string }) {
    this.track(EventENUM.INTERESTED_IN_PROPERTY_PICTURES, {
      Index: data.index,
      "Property ID": data.propertyId,
    })
  }

  SAVE_TO_FAVOURITES(data: {
    city?: string
    country?: string
    favouriteListId?: string | number
    favouriteListName?: string
    isFavourite?: boolean
    isFavouriteList?: boolean
    propertyId?: string
    propertyName?: string
    state?: string
    userName?: string
  }) {
    this.track(EventENUM.SAVE_TO_FAVOURITES, {
      Channel: "web",
      "Property Name": data.propertyName || "NA",
      "Property ID": data.propertyId || "NA",
      "User Name": data.userName || "NA",
      "City of property": data.city || "NA",
      "State of property": data.state || "NA",
      "Country of property": data.country || "NA",
      "Is Favorite": data.isFavourite || false,
      "Is Favorite List": data.isFavouriteList || false,
      "Favorite List Name": data.favouriteListName || "NA",
      "Favorite List ID": data.favouriteListId || "NA",
    })
  }

  VIEW_PROPERTY_DETAIL_SCREEN(data: {
    bathrooms: number
    bedrooms: number
    propertyId: string
    propertyName: string
  }) {
    this.track(EventENUM.VIEW_PROPERTY_DETAIL_SCREEN, {
      "Property Id": data.propertyId,
      "Property Name": data.propertyName,
      Bathrooms: data.bathrooms,
      Bedrooms: data.bedrooms,
    })
  }

  LIST_YOUR_PROPERTY_FORM_SUBMITTED(data: {
    PMS: string
    companyName: string
    email: string
    firstName: string
    lastName: string
    phoneNumber: string
    state: string
    units: number | null
    website: string
    jobTitle: string
    destinationCities: string
  }) {
    this.track(EventENUM.LIST_YOUR_PROPERTY_FORM_SUBMITTED, {
      "First Name": data.firstName,
      "Last Name": data.lastName,
      Company: data.companyName,
      state: data.state,
      Email: data.email,
      Phone: data.phoneNumber,
      Website: data.website,
      LEADCF2: data.PMS,
      LEADCF51: data.units,

      "job title": data.jobTitle,
      "destination cities": data.destinationCities,
    })
  }

  BOOK_NOW_OR_CONTINUE_BOOKING_CLICKED(data: {
    checkInDate: string
    checkOutDate: string
    city: string
    damageDeposit: number
    daystoCheckInDate: number
    guest: number
    hostFees: string
    location: string
    nightlyRate: string
    noOfNight: number
    propertyId: string
    propertyName: string
    prequoteID: string
    propertySize: number
    rent: number
    reviews: number
    saveAmount: number
    serviceFee: number
    state: string
    totalTax: number
    typeOfProperty: string
  }) {
    this.track(EventENUM.BOOK_NOW_OR_CONTINUE_BOOKING_CLICKED, {
      "Property ID": data.propertyId,
      "Property Name": data.propertyName,
      "Prequote ID": data.prequoteID,
      Location: data.location,
      "booking of city": data.city,
      "booking of State": data.state,
      "Check-In Date": data.checkInDate,
      "Check-Out Date": data.checkOutDate,
      "# of Nights": data.noOfNight,
      "# Days to Check-In Date": data.daystoCheckInDate,
      Guests: data.guest,
      "Nightly Rate Fees": data.nightlyRate,
      Rent: data.rent,
      hostFee: data.hostFees,
      totalTax: data.totalTax,
      damageDeposit: data.damageDeposit,
      serviceFee: data.serviceFee,
      saveAmount: data.saveAmount,
      "# of Reviews": data.reviews,
      "Type of Property": data.typeOfProperty,
      "Property Size": data.propertySize,
      "split payment click": false,
    })
  }

  BILLING_INFORMATION(data: {
    address?: string
    city?: string
    contactNumber?: string
    country?: string
    email?: string
    firstName?: string
    lastName?: string
    state?: string
    zipCode?: string
  }) {
    this.track(EventENUM.BILLING_INFORMATION, {
      "First Name": data.firstName,
      "Last Name": data.lastName,
      Email: data.email,
      "Contact Number": data.contactNumber,
      Address: data.address,
      Country: data.country,
      ZipCode: data.zipCode,
      "Billing User City": data.city,
      "Billing User State": data.state,
      "Split Payment": false,
    })
  }

  REFUNDABLE_BOOKING_SELECTED() {
    this.track(EventENUM.REFUNDABLE_BOOKING_SELECTED)
  }

  PROMOCODE_APPPLIED(data: {
    discount: number
    promoCode: string
    promoCodeId: number
  }) {
    this.track(EventENUM.PROMOCODE_APPPLIED, {
      "Promocode ID": data.promoCodeId,
      Promocode: data.promoCode,
      "Promo offer Amount": data.discount.toFixed(2),
    })
  }

  PROMOCODE_REMOVED(data: {
    discount: number
    promoCode: string
    promoCodeId: number
  }) {
    this.track(EventENUM.PROMOCODE_REMOVED, {
      "Promocode ID": data.promoCodeId,
      Promocode: data.promoCode,
      "Promo offer Amount": data.discount.toFixed(2),
    })
  }

  PAY_NOW(data: {
    bookingAmount: number
    prequoteID: string
    daysToCheckInDate: number
    guest: number
    numberOfNight: number
    paymentMode: string
  }) {
    this.track(EventENUM.PAY_NOW, {
      "Prequote ID": data.prequoteID,
      "Total amount payment": data.bookingAmount.toFixed(2),
      "Split payment": false,
      "Split payment Amount": (0).toFixed(2),
      "# of night": data.numberOfNight,
      guest: data.guest,
      "# Days to Check-In Date": data.daysToCheckInDate,
      "Payment Type": data.paymentMode,
    })
  }

  TOTAL_PAYMENT(data: {
    checkInDate: string
    checkOutDate: string
    city: string
    daysToCheckInDate: number
    guest: number
    hostFees: number
    nightlyRate: number
    noOfBedroom: number
    numberOfNight: number
    paymentMode: string
    propertyType: string
    prequoteID: string
    state: string
    totalAmount: number
    totalTax: number | string
  }) {
    this.track(EventENUM.TOTAL_PAYMENT, {
      "Check-In Date": data.checkInDate,
      "Check-Out Date": data.checkOutDate,
      Guests: data.guest,
      "Host Fees": data.hostFees,
      "Nightly Rate": data.nightlyRate,
      totalTax: data.totalTax,
      totalAmount: data.totalAmount.toFixed(2),
      "booking of city": data.city,
      "booking of State": data.state,
      "Type of Property": data.propertyType,
      "Prequote ID": data.prequoteID,
      "Property Size": data.noOfBedroom,
      "Split payment": false,
      "Split payment Amount": Number(0).toFixed(2),
      "# of night": data.numberOfNight,
      "# Days to Check-In Date": data.daysToCheckInDate,
      "Payment Type": data.paymentMode,
    })
  }

  BOOKING_TRACKER(data: {
    bookingPayload: BookingRequest | BookingPayloadType
    function: string
  }) {
    this.track(EventENUM.BOOKING_TRACKER, {
      function: data.function,
      bookingPayload: data.bookingPayload,
    })
  }

  COMPLETED_BOOKING(data: {
    bookingNumber: string
    checkInDate: string
    checkOutDate: string
    city: string
    daysToCheckInDate: number
    guest: number
    isPaymentOverTime: boolean
    noOfBedroom: number
    numberOfNight: number
    paymentTimeePeriod: number
    paymentTimeePeriodAmount: number
    prequoteID: string
    propertyType: string
    refundableBooking: boolean
    state: string
    totalAmount: number
  }) {
    this.track(EventENUM.COMPLETED_BOOKING, {
      "Booking Number": data.bookingNumber,
      "check in date": data.checkInDate,
      "check out date": data.checkOutDate,
      "Prequote ID": data.prequoteID,
      "City of Booking": data.city,
      "State of Booking": data.state,
      "Total payment": data.totalAmount.toFixed(2),
      "Split payment": false,
      "# of night": data.numberOfNight,
      "Refundable Booking": data.refundableBooking,
      guest: data.guest,
      "Property Size": data.noOfBedroom,
      "Split payment Amount": Number(0).toFixed(2),
      "# Days to Check-In Date": data.daysToCheckInDate,
      "Invited Guests": [],
      "Is Payment Over Time(Affirm)": data.isPaymentOverTime,
      "Payment Time Period(Affirm)": data.paymentTimeePeriod,
      "Payment Time Period Amount(Affirm)":
        data.paymentTimeePeriodAmount.toFixed(2),
    })
  }

  BOOKING_FLOW_ERR(data: { err: unknown }) {
    this.track(EventENUM.BOOKING_FLOW_ERR, {
      response: data.err,
    })
  }

  BOOKING_ERROR(data: {
    errorDisplay?: unknown
    isPaymentOverTime?: boolean
    propertyId: string
    prequoteID: string
    typeOfError: unknown
  }) {
    this.track(EventENUM.BOOKING_ERROR, {
      "Type of Error": data.typeOfError || "",
      "Error Display": data.errorDisplay || "",
      "Property ID": data.propertyId,
      "Prequote ID": data.prequoteID,
      "Is Payment Over Time": data.isPaymentOverTime || false,
    })
  }

  PREQUOTE_TIMEOUT_ERR(data: { errDisplay: string; errResponse: string }) {
    this.track(EventENUM.PREQUOTE_TIMEOUT_ERR, {
      "Error Display": data.errDisplay,
      "Error Response": data.errResponse,
    })
  }

  BOOKING_STORE_PREQUOTE_ERROR(data: {
    adultNumber: number
    childNumber: number
    dayCount: number
    errorMessage: string
    petsAllowed: boolean
    propertyId: string
    startDate: string
    pmcId: string
    pmsSystem: string
  }) {
    if (data.errorMessage === "Network Error") return
    this.track(EventENUM.BOOKING_STORE_PREQUOTE_ERROR, {
      "Property ID": data.propertyId,
      "Adults Count": data.adultNumber,
      "Children Count": data.childNumber,
      "Pets Allowed?": data.petsAllowed,
      startDate: data.startDate,
      dayCount: data.dayCount,
      errorMessage: data.errorMessage,
      pmcId: data.pmcId,
      pmsSystem: data.pmsSystem,
    })
  }

  BOOKING_STORE_INVALID_PREQUOTE_REQUEST(data: {
    adultNumber: number
    childNumber: number
    dayCount: number
    errorMessage: string
    petsAllowed: boolean
    propertyId: string
    startDate: string
    pmcId: string
    pmsSystem: string
    quoteError: boolean
  }) {
    this.track(EventENUM.BOOKING_STORE_INVALID_PREQUOTE_REQUEST, {
      "Property ID": data.propertyId,
      "Adults Count": data.adultNumber,
      "Children Count": data.childNumber,
      "Pets Allowed?": data.petsAllowed,
      startDate: data.startDate,
      dayCount: data.dayCount,
      errorMessage: data.errorMessage,
      pmcId: data.pmcId,
      pmsSystem: data.pmsSystem,
      quoteError: data.quoteError,
    })
  }

  BOOKING_STORE_SUCCESS(data: {
    adultNumber: number
    childNumber: number
    dayCount: number
    petsAllowed: boolean
    propertyId: string
    prequoteID: string
    startDate: string
  }) {
    this.track(EventENUM.BOOKING_STORE_SUCCESS, {
      "Property ID": data.propertyId,
      "Prequote ID": data.prequoteID,
      "Adults Count": data.adultNumber,
      "Children Count": data.childNumber,
      "Pets Allowed?": data.petsAllowed,
      startDate: data.startDate,
      dayCount: data.dayCount,
    })
  }

  SKIP_MORE_GUESTS_SUBMISSION(data: { source: string; splitPayment: boolean }) {
    this.track(EventENUM.SKIP_MORE_GUESTS_SUBMISSION, {
      "Split Payment": data.splitPayment,
      Source: data.source,
    })
  }

  BOOKING_SURVEY(data: {
    bookingNumber: string
    chooseToBook: string
    easyToBook: string
    happyWithStay: string
    likelyToRecommend: number
    rateToTheProperty: number
    yourFeedback: string
  }) {
    this.track(EventENUM.BOOKING_SURVEY, {
      "Choose To Book": data.chooseToBook,
      "Easy To Book": data.easyToBook,
      "Happy With Stay": data.happyWithStay,
      "Likely To Recommend": data.likelyToRecommend,
      "Rate To The Property": data.rateToTheProperty,
      "Your Feedback": data.yourFeedback,
      "Booking Number": data.bookingNumber,
    })
  }

  // Precision Pricing module triggered from the property details page
  SHOW_PRECISION_PRICING(data: {
    propertyId: string
    apiStatus: string
    airbnbUrl: string
    airbnbTotal: number | string
    airbnbError: string
    vrboUrl: string
    vrboTotal: number | string
    vrboError: string
    errorMessage?: string
    precisionPriceShown?: boolean
  }) {
    this.track(EventENUM.SHOW_PRECISION_PRICING, {
      "Property ID": data.propertyId,
      "API Status": data.apiStatus,
      "Airbnb URL": data.airbnbUrl,
      "Airbnb Total": data.airbnbTotal,
      "Airbnb Error": data.airbnbError,
      "VRBO URL": data.vrboUrl,
      "VRBO Total": data.vrboTotal,
      "VRBO Error": data.vrboError,
      "Error Message": data.errorMessage || "",
      "precision price shown": data.precisionPriceShown || false,
    })
  }
}

export const mixpanelV1Client = new MixpanelV1()
